import { Button, ButtonGroup } from '@mui/material';
import { ColDef, GetRowIdParams, ICellRendererParams } from 'ag-grid-community';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { LocationModel, TierModel } from '../../../../Generated/Commodity-Management-Api';
import { useLoadingState } from '../../../../Hooks';
import { StoneXDivider, StoneXGrid, StoneXRow } from '../../../StoneX';
import StoneXChart from '../../../StoneX/StoneXChart';
import { StoneXModal, StoneXSelect, StoneXTextField } from '../../../StoneXMui';
import { useSnackbar } from 'notistack';
import { RootState, useApplicationDispatch, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { addLocation as addLocationRedux, updateLocation as updateLocationRedux, removeLocation as removeLocationRedux, removeTier as removeTierRedux, addTier as addTierRedux } from '../../../../Redux/Slices/CommodityManagementSlice';
import { UserAccessState } from '../../../../Redux/Slices/UserAccessSlice';
import { AgGridReact } from 'ag-grid-react';
import Highcharts from 'highcharts';
import HighchartsSankey from 'highcharts/modules/sankey';
import HighchartsOrganization from 'highcharts/modules/organization';

HighchartsSankey(Highcharts);
HighchartsOrganization(Highcharts);
interface OrganizationSettingsProps {
  tiers: TierModel[];
  locations: LocationModel[];
}

export default function OrganizationSettings(props: OrganizationSettingsProps) {
  const dispatch = useApplicationDispatch();
  const userAccess = useApplicationSelector((state: RootState): UserAccessState => state.userAccess);
  const { enqueueSnackbar } = useSnackbar();
  const [tiers, setTiers] = useState<TierModel[]>(props.tiers);
  const [locations, setLocations] = useState<LocationModel[]>(props.locations);
  const [locationModalOpen, setLocationModalOpen] = useState<boolean>(false);
  const [tierModalOpen, setTierModalOpen] = useState<boolean>(false);
  const [modalTier, setModalTier] = useState<TierModel | undefined>();
  const [modalLocation, setModalLocation] = useState<LocationModel | undefined>();
  const saveLoadingState = useLoadingState();

  const orgChartOptions = useMemo((): Highcharts.Options => {
    const sorted = [...locations].sort((a, b) => {
      const pa = a.parentLocationId ?? -1;
      const pb = b.parentLocationId ?? -1;
      if (pa !== pb) return pa - pb;
      return a.locationId - b.locationId;
    });

    const nodes: any[] = sorted.map((l) => ({
      id: String(l.locationId),
      name: l.locationName,
    }));

    const links: any[] = sorted
      .filter((l) => l.parentLocationId != null)
      .map((l) => ({ from: String(l.parentLocationId), to: String(l.locationId) }));

    return {
      chart: { type: 'organization', inverted: true },
      title: { text: 'Organization Chart' },
      series: [{
        type: 'organization',
        name: 'Locations',
        keys: ['from', 'to'],
        data: links,
        nodes,
        colorByPoint: false,
        color: 'var(--dark-secondary-blue',
        dataLabels: { color: 'white' },
        borderColor: 'white',
        nodeWidth: 65,
      }] as Highcharts.SeriesOptionsType[],
      tooltip: { outside: false },
      credits: { enabled: false },
    };
  }, [locations]);

  const columnDefsTier: ColDef[] = [
    {
      headerName: 'Number',
      field: 'tierRank',
      sortable: true,
      width: 100,
      filter: false,
    },
    {
      headerName: 'Tier Name',
      field: 'tierName',
      sortable: true,
      flex: 1,
      filter: false,
    },
    {
      headerName: 'Actions',
      width: userAccess.isAdmin ? 180 : 100,
      cellRenderer: (params: any) => (
        <ButtonGroup variant="text">
          <Button onClick={() => openEditTier(params.data)}>Edit</Button>
          {userAccess.isAdmin && <Button onClick={() => deleteTier(params.data)}>Delete</Button>}
        </ButtonGroup>
      ),
    },
  ];

  function getLocationColumnDefs(tier: TierModel): ColDef[] {
    return [
      {
        headerName: 'Location Name',
        field: 'locationName',
        sortable: true,
        flex: 1,
        filter: false,
      },
      {
        headerName: tier.tierRank === 1 ? '' : (tiers.find(t => t.tierRank === (tier.tierRank ?? 0) - 1)?.tierName ?? 'Parent'),
        field: 'parentLocationId',
        hide: tier.tierRank === 1,
        sortable: true,
        flex: 1,
        filter: false,
        valueFormatter: (params) => locations.find(l => l.locationId === params.value)?.locationName ?? '',
      },
      {
        headerName: 'Actions',
        width: userAccess.isAdmin ? 240 : 100,
        cellRenderer: (params: any) => (
          <ButtonGroup variant="text">
            <Button onClick={() => openEditLocation(tier, params.data)}>Edit</Button>
            {userAccess.isAdmin && <Button onClick={() => toggleLocationActive(params.data)}>{params.data.active ? 'Disable' : 'Activate'}</Button>}
            {userAccess.isAdmin && <Button onClick={() => deleteLocation(params.data)}>Delete</Button>}
          </ButtonGroup>
        ),
      },
    ];
  }

  function saveTier(tierToSave: Partial<TierModel>) {
    if (!tierToSave.tierId) {
      const rankToUse = tierToSave.tierRank ?? Math.max(0, ...tiers.map(t => t.tierRank)) + 1;
      CommodityManagementApi.addTier(
        saveLoadingState.setLoadingState,
        rankToUse,
        tierToSave.tierName,
      ).then((res) => {
        setTiers(prev => [...prev, res.data.tier as TierModel]);
        dispatch(addTierRedux({ tier: res.data.tier! }));
        enqueueSnackbar('Tier added successfully', { variant: 'success' });
      }).catch(() => {
        enqueueSnackbar('Failed to add tier', { variant: 'error' });
      });
      return;
    }
    CommodityManagementApi.updateTier(
      saveLoadingState.setLoadingState,
      tierToSave.tierId.toString(),
      tierToSave.tierName!,
      tierToSave.tierRank!,
    ).then((res) => {
      setTiers(prev => prev.map(t => t.tierId === res.data.tier?.tierId ? (res.data.tier as TierModel) : t));
      enqueueSnackbar('Tier updated successfully', { variant: 'success' });
    }).catch(() => {
      enqueueSnackbar('Failed to update tier', { variant: 'error' });
    });
  }

  function deleteTier(tierToDelete: TierModel) {
    const tierToDeleteId = tierToDelete.tierId!;
    CommodityManagementApi.deleteTier(null, tierToDeleteId.toString()).then(() => {
      setTiers(prev => prev.filter(t => t.tierId !== tierToDeleteId));
      dispatch(removeTierRedux({ tierId: tierToDeleteId }));
      enqueueSnackbar('Tier deleted successfully', { variant: 'success' });
    }).catch(() => {
      enqueueSnackbar('Failed to delete tier', { variant: 'error' });
    });
  }

  function saveLocation(locationToSave: Partial<LocationModel>) {
    if (!locationToSave.locationId) {
      CommodityManagementApi.addLocation(
        saveLoadingState.setLoadingState,
        locationToSave.locationName!,
        locationToSave.tierId!,
        locationToSave.parentLocationId ?? undefined,
      ).then((res) => {
        setLocations(prev => [...prev, res.data.location as LocationModel]);
        dispatch(addLocationRedux({ location: res.data.location! }));
        enqueueSnackbar('Location added successfully', { variant: 'success' });
      }).catch(() => {
        enqueueSnackbar('Failed to add location', { variant: 'error' });
      });
    } else {
      CommodityManagementApi.updateLocation(
        saveLoadingState.setLoadingState,
        locationToSave.locationId.toString(),
        locationToSave.locationName!,
        locationToSave.tierId!,
        locationToSave.parentLocationId ?? undefined,
        locationToSave.active,
      ).then((res) => {
        setLocations(prev => prev.map(l => l.locationId === res.data.location?.locationId ? (res.data.location as LocationModel) : l));
        dispatch(updateLocationRedux({ location: res.data.location! }));
        enqueueSnackbar('Location updated successfully', { variant: 'success' });
      }).catch(() => {
        enqueueSnackbar('Failed to update location', { variant: 'error' });
      });
    }
  }

  function deleteLocation(locationToDelete: LocationModel) {
    const locationToDeleteId = locationToDelete.locationId!;
    CommodityManagementApi.deleteLocation(null, locationToDeleteId.toString()).then(() => {
      setLocations(prev => prev.filter(l => l.locationId !== locationToDeleteId));
      dispatch(removeLocationRedux({ locationId: locationToDeleteId }));
    }).catch(() => {
      enqueueSnackbar('Failed to delete location', { variant: 'error' });
    });
  }

  function toggleLocationActive(location: LocationModel) {
    saveLocation({ ...location, active: !location.active });
  }

  function openEditTier(tier: TierModel) {
    setModalTier({ ...tier });
    setTierModalOpen(true);
  }

  function openAddTier() {
    setModalTier(undefined);
    setTierModalOpen(true);
  }

  function openAddLocation(tier: TierModel) {
    setModalTier(tier);
    setModalLocation(undefined);
    setLocationModalOpen(true);
  }

  function openEditLocation(tier: TierModel, location: LocationModel) {
    setModalTier(tier);
    setModalLocation({ ...location });
    setLocationModalOpen(true);
  }

  return (
    <>
      <StoneXRow align="end">
        <h3>Tiers</h3>
        <Button className="pull-right" variant="outlined" onClick={openAddTier}>Add</Button>
      </StoneXRow>
      <StoneXGrid
        hideGridOptions
        getRowId={(params: GetRowIdParams) => params.data.tierId}
        columns={columnDefsTier}
        rows={tiers}
      />

      <StoneXDivider />

      {tiers.filter(x => x.tierRank != 1).map((tier) => (
        <div key={tier.tierId}>
          <StoneXRow align="end">
            <h3>{tier.tierName}</h3>
            <Button className="pull-right" variant="outlined" onClick={() => openAddLocation(tier)}>
              Add
            </Button>
          </StoneXRow>
          <StoneXGrid
            hideGridOptions
            getRowId={(params: GetRowIdParams) => params.data.locationId}
            columns={getLocationColumnDefs(tier)}
            rows={locations.filter(l => l.tierId === tier.tierId)}
            gridOptions={{
              onGridReady: (params) => {
                params.api.setFilterModel({ active: { filterType: 'set', values: [true] } });
              },
            }}
          />
          <br />
        </div>
      ))}

      <StoneXDivider />

      <StoneXRow justify='center'>
        <StoneXChart options={orgChartOptions} height={400} width={1000} />
      </StoneXRow>

      <UpdateTierModal
        open={tierModalOpen}
        onClose={() => setTierModalOpen(false)}
        onTierSave={saveTier}
        tier={modalTier}
      />

      <UpdateLocationModal
        open={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        onLocationSave={saveLocation}
        location={modalLocation}
        locations={locations}
        tier={modalTier!}
        tiers={tiers}
      />
    </>
  );
}

interface UpdateTierModalProps {
  open: boolean;
  onClose: () => void;
  onTierSave: (tier: Partial<TierModel>) => void;
  tier?: TierModel;
}

function UpdateTierModal(props: UpdateTierModalProps) {
  const { open, onClose, onTierSave, tier } = props;
  const actionLabel = tier ? 'Update' : 'Add';
  const [tierName, setTierName] = useState<string>();
  const [tierRank, setTierRank] = useState<number | ''>('');

  useEffect(() => {
    setTierName(tier?.tierName ?? '');
    setTierRank(tier?.tierRank ?? '');
  }, [tier]);

  function saveTier() {
    onTierSave({ ...tier, tierName, tierRank: tierRank === '' ? undefined : tierRank });
    onClose();
  }

  return (
    <StoneXModal header={`${actionLabel} Tier`} open={open} onClose={onClose}>
      <StoneXRow>
        <StoneXTextField label="Tier Rank" type="number" value={tierRank} onChange={(event: any) => setTierRank(event.target.value === '' ? '' : Number(event.target.value))} />
        <StoneXTextField label="Tier Name" value={tierName} onChange={(event: any) => setTierName(event.target.value)} />
      </StoneXRow>
      <StoneXRow justify="end">
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button onClick={saveTier}>{`${actionLabel} Tier`}</Button>
      </StoneXRow>
    </StoneXModal>
  );
}

interface UpdateLocationModalProps {
  open: boolean;
  onClose: () => void;
  onLocationSave: (location: Partial<LocationModel>) => void;
  location?: LocationModel;
  locations: LocationModel[];
  tier: TierModel;
  tiers: TierModel[];
}

function UpdateLocationModal(props: UpdateLocationModalProps) {
  const { open, onClose, onLocationSave, location, locations, tier, tiers } = props;

  const actionLabel = props.location ? 'Update' : 'Add';
  const [locationName, setLocationName] = useState<string>();
  const [selectedParentLocation, setSelectedParentLocation] = useState<LocationModel | null>(null);

  const parentTier = useMemo(() => tiers.find(t => t.tierRank === (tier?.tierRank ?? 0) - 1), [tier, tiers]);
  const parentLocations = useMemo(() => locations.filter(l => l.tierId === parentTier?.tierId), [locations, parentTier]);

  useEffect(() => {
    setLocationName(location?.locationName ?? '');
    setSelectedParentLocation(locations.find(l => l.locationId === location?.parentLocationId) ?? null);
  }, [location, tier]);

  function saveLocation() {
    onLocationSave({
      locationId: location?.locationId ?? 0,
      locationName,
      tierId: tier?.tierId,
      parentLocationId: selectedParentLocation?.locationId ?? undefined,
      active: location?.active ?? true,
    });
    onClose();
  }

  return (
    <StoneXModal header={`${actionLabel} Location`} open={open} onClose={onClose}>
      {parentTier && (
        <StoneXRow>
          <StoneXSelect
            label={parentTier.tierName!}
            value={selectedParentLocation}
            options={parentLocations}
            getId={(l) => l.locationId}
            getOptionLabel={(l) => l.locationName}
            onChange={(l: LocationModel | null) => setSelectedParentLocation(l)}
          />
        </StoneXRow>
      )}
      <StoneXRow>
        <StoneXTextField label="Location Name" value={locationName} onChange={(event: any) => setLocationName(event.target.value)} />
      </StoneXRow>
      <StoneXRow justify="end">
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button onClick={saveLocation}>{`${actionLabel} Location`}</Button>
      </StoneXRow>
    </StoneXModal>
  );
}